import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {object} CartProtectionRefs
 * @property {HTMLInputElement} toggle - The opt-in checkbox.
 * @property {HTMLElement} error - Live region shown when an update fails.
 */

/**
 * Set by assets/cart-protected-checkout.js right before it sends the customer to checkout with
 * package protection included. Shopify only clears a cart automatically on a completed order —
 * if the customer abandons checkout instead (back button, closed tab, switched away and never
 * came back to pay), the add on line is still sitting in their cart exactly as added, and they
 * should never see it show up on their own after walking away from a checkout they didn't
 * finish. This flag is how we tell "we're back, and that checkout never completed" apart from
 * every other page load.
 * @constant {string}
 */
const PENDING_CHECKOUT_FLAG = 'cartProtectionPendingCheckout';

/**
 * How long an add or remove may take before it counts as failed. Without a limit, a request
 * stalled on a weak mobile connection keeps the toggle disabled indefinitely, and
 * assets/cart-protected-checkout.js waits on that toggle before submitting, so both checkout
 * buttons stayed dead until the page was reloaded.
 * @constant {number}
 */
const REQUEST_TIMEOUT_MS = 10000;

/**
 * Un-checks the toggle (and lets its own change handler remove the cart line for real) if the
 * customer is back from an abandoned protected checkout. toggle.checked is server rendered fresh
 * on every page load from the actual cart state, so this only ever acts when the add on is
 * genuinely still there — a completed order already emptied the cart (no toggle, or unchecked),
 * and a customer who removed it some other way in the meantime is left alone.
 */
function cleanupAbandonedProtectionCheckout() {
  if (!sessionStorage.getItem(PENDING_CHECKOUT_FLAG)) return;
  sessionStorage.removeItem(PENDING_CHECKOUT_FLAG);

  const toggle = document.querySelector('cart-protection [ref="toggle"]');
  if (toggle instanceof HTMLInputElement && toggle.checked && !toggle.disabled) {
    toggle.checked = false;
    toggle.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// A normal same tab return (back button, closed checkout tab) re-runs this module on the fresh
// page load, caught below in connectedCallback. These two cover what that misses: pageshow
// fires on a bfcache restore (back button restoring the page without actually re-executing any
// script), and visibilitychange fires when the customer switches back to an already open tab
// (checkout opened in a new tab, e.g. some accelerated checkout flows) without any navigation at
// all happening on this one — including while they're sitting there looking at an already open
// cart drawer, which otherwise would never re-check anything on its own.
window.addEventListener('pageshow', cleanupAbandonedProtectionCheckout);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') cleanupAbandonedProtectionCheckout();
});

/**
 * An optional cart add-on the customer opts into — package protection, a
 * warranty, gift wrap.
 *
 * Adds or removes the configured product as a cart line carrying an `_add_on`
 * line item property, so downstream surfaces can identify it by property
 * instead of matching a translated product title.
 *
 * The cart request asks for the cart sections back in the same response and
 * morphs them, so a single round trip updates the drawer and the cart page
 * together.
 *
 * @extends {Component<CartProtectionRefs>}
 */
class CartProtection extends Component {
  requiredRefs = ['toggle', 'error'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    // Covers the common case directly: a fresh page load (this element connecting for the
    // first time) after the customer comes back from an abandoned checkout. See
    // cleanupAbandonedProtectionCheckout's own doc comment for the two listeners that cover
    // what a fresh connect misses.
    cleanupAbandonedProtectionCheckout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
  }

  /**
   * Brings the cart in line with the toggle the customer just changed.
   * @param {Event} event - The change event on the toggle.
   */
  toggleAddOn = async (event) => {
    const { toggle, error } = this.refs;
    const { variantId, lineKey, addOnKey } = this.dataset;
    const adding = toggle.checked;

    if (!variantId) return;

    if (!adding && !lineKey) {
      toggle.checked = true;
      return;
    }

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    toggle.disabled = true;
    toggle.removeAttribute('aria-invalid');
    error.hidden = true;

    const sections = this.#sectionsToRender();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      adding
        ? new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart',
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: deferred.promise,
          })
        : new CartLinesUpdateEvent({
            action: 'remove',
            context: 'cart',
            lines: [{ id: /** @type {string} */ (lineKey), quantity: 0 }],
            promise: deferred.promise,
          })
    );

    const body = adding
      ? {
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: { _add_on: addOnKey || 'protection' },
            },
          ],
          sections,
          sections_url: window.location.pathname,
        }
      : { id: lineKey, quantity: 0, sections, sections_url: window.location.pathname };

    /** @type {any} */
    let data;

    const timeout = setTimeout(() => abortController.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(adding ? Theme.routes.cart_add_url : Theme.routes.cart_change_url, {
        ...fetchConfig('json', { body: JSON.stringify(body) }),
        signal: abortController.signal,
      });

      data = await response.json();

      if (data.status || data.errors) {
        const errorDetail = typeof data.errors === 'string' ? data.errors : null;
        throw new Error(data.description || data.message || errorDetail || 'Cart update failed');
      }
    } catch (updateError) {
      // Only a newer toggle replacing this request is a silent abort: that request now owns the
      // toggle. A timeout or a disconnect is a failure, so the toggle is re-enabled below
      // instead of staying disabled forever.
      const superseded = this.#activeFetch !== abortController;
      if (updateError instanceof Error && updateError.name === 'AbortError' && superseded) return;

      toggle.checked = !adding;
      toggle.disabled = false;
      toggle.setAttribute('aria-invalid', 'true');
      error.hidden = false;
      this.#activeFetch = null;
      deferred.reject(updateError);

      this.dispatchEvent(
        new CartErrorEvent({
          error: updateError instanceof Error ? updateError.message : 'Failed to update the cart add-on',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      return;
    } finally {
      clearTimeout(timeout);
    }

    for (const [sectionId, html] of Object.entries(data.sections ?? {})) {
      try {
        await morphSection(sectionId, /** @type {string} */ (html));
      } catch (morphError) {
        console.warn('[cart-protection] Could not morph section', sectionId, morphError);
      }
    }

    toggle.disabled = false;
    this.#activeFetch = null;

    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data) });
    cartPerformance.measureFromEvent(`add-on-${adding ? 'add' : 'remove'}:user-action`, event);
  };

  /**
   * Every cart section on the page, so one request re-renders the drawer and
   * the cart page together.
   * @returns {string} Comma-separated section ids.
   */
  #sectionsToRender() {
    const ids = new Set();

    if (this.dataset.sectionId) ids.add(this.dataset.sectionId);

    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        ids.add(element.dataset.sectionId);
      }
    }

    return Array.from(ids).join(',');
  }
}

if (!customElements.get('cart-protection')) {
  customElements.define('cart-protection', CartProtection);
}
